<script setup>
import { computed, ref, watch } from 'vue';
import { useStoreGetters } from 'dashboard/composables/store';

// Indicadores do ecossistema próprio (ODFlow/ODMind) no painel de detalhes:
// dias de memória, orçamento estimado, etapa do funil e alerta de dado
// sensível. Roda dentro do Vue do Chatwoot (não é iframe/Dashboard App),
// então busca via fetch direto no ODFlow com credentials:'include' (cookie
// de sessão same-site, ver notes/0020 no SecondBrain), CORS restrito no
// lado do ODFlow.
const FUNNEL_STEPS = [
  'lead_new',
  'lead_qualified',
  'quote_prepared',
  'quote_sent',
  'waiting_client',
  'order_started',
  'order_submitted',
  'waiting_signature',
  'signed',
  'payment_review',
  'paid',
  'issued',
  'delivered',
  'completed',
];

const currency = new Intl.NumberFormat('pt-BR', {
  style: 'currency',
  currency: 'BRL',
});

const getters = useStoreGetters();
const currentChat = computed(() => getters.getSelectedChat.value);
const conversationId = computed(() => currentChat.value?.id);

const summary = ref(null);
const isLoading = ref(false);

async function fetchSummary(id) {
  if (!id) {
    summary.value = null;
    return;
  }
  isLoading.value = true;
  try {
    const response = await fetch(
      `https://app.odtravel.com.br/api/dashboard-apps/lead-summary?conversationId=${encodeURIComponent(id)}`,
      { credentials: 'include' }
    );
    summary.value = response.ok ? await response.json() : null;
  } catch {
    summary.value = null;
  } finally {
    isLoading.value = false;
  }
}

watch(conversationId, id => fetchSummary(id), { immediate: true });

const funnelIndex = computed(() => {
  const status = summary.value?.lead?.status;
  return status ? FUNNEL_STEPS.indexOf(status) : -1;
});
const funnelPercent = computed(() =>
  funnelIndex.value >= 0
    ? Math.round(((funnelIndex.value + 1) / FUNNEL_STEPS.length) * 100)
    : 0
);
const isLost = computed(() => summary.value?.lead?.status === 'lost');
</script>

<template>
  <div v-if="summary" class="flex flex-col gap-2 mb-4 text-xs">
    <div
      v-if="summary.insight?.hasSensitiveData"
      class="rounded-lg bg-n-ruby-3 text-n-ruby-11 px-2 py-1.5 font-medium"
    >
      {{ '⚠️ Dado sensível detectado nesta conversa' }}
    </div>

    <div v-if="summary.memoryDays !== null" class="text-n-slate-11">
      {{
        summary.memoryDays === 0
          ? '🆕 Conversa nova'
          : `📅 ${summary.memoryDays} dia(s) de histórico`
      }}
    </div>

    <div v-if="summary.lead?.estimatedBudget" class="text-n-slate-11">
      {{
        '💰 Orçamento estimado: ' +
        currency.format(Number(summary.lead.estimatedBudget))
      }}
    </div>

    <div v-if="isLost" class="text-n-ruby-11 font-medium">
      {{ '🔴 Lead perdido' }}
    </div>
    <div v-else-if="funnelIndex >= 0" class="flex flex-col gap-1">
      <div class="h-1.5 w-full rounded-full bg-n-slate-4 overflow-hidden">
        <div
          class="h-full bg-n-teal-9"
          :style="{ width: `${funnelPercent}%` }"
        />
      </div>
      <span class="text-n-slate-10">{{ summary.lead.status }}</span>
    </div>
  </div>
</template>
